// ---------------------------------------------------------------------------
// Display formatting, icon mapping, and UI variant dispatch
// ---------------------------------------------------------------------------

import {
  Ruler,
  Circle,
  Layers,
  MoveHorizontal,
  Move,
  Palette,
  Hash,
  Compass,
  RotateCw,
  Maximize,
  Minimize,
  GripHorizontal,
  ChevronRight,
  Box,
  Cylinder,
  Cone,
  Hexagon,
  Square,
  Triangle,
  Settings,
  Upload,
  LucideIcon,
  ArrowRightLeft,
  Type,
} from 'lucide-react';
import type { Parameter, ParameterOption } from '@shared/types';

/**
 * Converts a variable name to a human-readable display label.
 * - Replaces underscores with spaces
 * - Capitalizes the first letter of each word
 * - Preserves existing camelCase boundaries
 * Examples: width -> Width, box_height -> Box Height, wallThickness -> Wall Thickness
 */
export function generateDisplayName(name: string): string {
  if (!name) return '';

  // Handle snake_case: replace underscores with spaces
  let result = name.replace(/_/g, ' ');

  // Handle camelCase: insert space before uppercase letters (except the first one)
  result = result.replace(/([a-z])([A-Z])/g, '$1 $2');

  // Capitalize the first letter of each word
  result = result.replace(/\b\w/g, (char) => char.toUpperCase());

  return result;
}

// Icon mapping for parameter types/names
export const PARAMETER_ICON_MAP: Record<string, LucideIcon> = {
  // Measurements
  width: Ruler,
  height: Ruler,
  length: Ruler,
  depth: Ruler,
  thickness: Layers,
  size: Maximize,
  // Radius/Diameter
  radius: Circle,
  diameter: Circle,
  // Position/Offset
  offset: Move,
  spacing: MoveHorizontal,
  gap: MoveHorizontal,
  distance: MoveHorizontal,
  margin: GripHorizontal,
  padding: GripHorizontal,
  inset: Minimize,
  // Count/Quantity
  count: Hash,
  number: Hash,
  quantity: Hash,
  segments: Hash,
  sides: Hash,
  // Angles/Rotation
  angle: Compass,
  rotation: RotateCw,
  rotate: RotateCw,
  // Colors & Materials
  color: Palette,
  material: Layers,
  // Shapes
  box: Box,
  cube: Box,
  cylinder: Cylinder,
  cone: Cone,
  hexagon: Hexagon,
  square: Square,
  triangle: Triangle,
  // Resolution
  $fn: Circle,
  fn: Circle,
  resolution: Circle,
  quality: Circle,
  // Generic
  default: Settings,
  // File
  file: Upload,
  upload: Upload,
  // Font
  font: Type,
  // Range
  range: ArrowRightLeft,
};

/**
 * Returns a Lucide icon component based on the parameter name.
 * Falls back to a generic chevron icon if no match found.
 */
export function getParameterIcon(paramName: string): LucideIcon {
  const normalizedName = paramName.toLowerCase().replace(/_/g, '');

  // Check for exact match first
  if (PARAMETER_ICON_MAP[normalizedName]) {
    return PARAMETER_ICON_MAP[normalizedName];
  }

  // Check if the name contains any of the keywords
  for (const [key, icon] of Object.entries(PARAMETER_ICON_MAP)) {
    if (normalizedName.includes(key)) {
      return icon;
    }
  }

  // Return default icon
  return ChevronRight;
}

/**
 * Determines if a parameter represents a physical measurement (e.g. in millimeters).
 */
export function isMeasurementParameter(param: Parameter): boolean {
  // Common measurement-related terms
  const measurementTerms = [
    'width',
    'height',
    'length',
    'depth',
    'thickness',
    'radius',
    'diameter',
    'distance',
    'size',
    'offset',
    'gap',
    'spacing',
    'margin',
    'padding',
    'inset',
    'extrude',
    'dimension',
    'wall',
  ];

  // Check if parameter name or displayName contains measurement terms
  const nameToCheck = param.name.toLowerCase();
  const displayNameToCheck = param.displayName.toLowerCase();

  // Only consider number type parameters
  if (param.type !== 'number' && param.type !== undefined) {
    return false;
  }

  // Check if the parameter name or display name contains any measurement terms
  return measurementTerms.some(
    (term) => nameToCheck.includes(term) || displayNameToCheck.includes(term),
  );
}

/**
 * Given a parsed Parameter, return a human-readable variant name that
 * describes which UI widget will be used to render it.
 *
 * The dispatch order mirrors ParameterInput.tsx so the result is always
 * consistent with what the user will actually see.
 */
export function identifyVariant(param: Parameter): string {
  const MAX_TOGGLE_GROUP_TOTAL_CHARS = 24;
  const MAX_TOGGLE_GROUP_OPTIONS = 3;

  const isSvgFileLabel = (label: string) =>
    label.toLowerCase().endsWith('.svg');
  const hasAllSvgOptions = (opts: ParameterOption[]) =>
    opts.length >= 2 && opts.every((o) => isSvgFileLabel(o.label));
  const shouldUseToggleGroup = (opts: ParameterOption[]) => {
    if (opts.length < 2 || opts.length > MAX_TOGGLE_GROUP_OPTIONS) return false;
    return (
      opts.reduce((s, o) => s + o.label.length, 0) <=
      MAX_TOGGLE_GROUP_TOTAL_CHARS
    );
  };

  if (param.type === 'markdown') return 'Markdown Block';
  if (param.type === 'camera_pip') return 'Camera Picture-in-Picture';
  if (param.type === 'code') return 'Code Expression';
  if (param.type === 'svgcheckboxgroup') return 'SVG Checkbox Group';
  if (param.type === 'svgradiogroup') return 'SVG Radio Group';
  if (param.type === 'color' && param.options && param.options.length > 0)
    return 'Color Swatches';
  if (param.type === 'grid') return 'Grid Input';
  if (param.type === 'stack') return 'Stack Input';
  if (param.type === 'stepper') return 'Stepper';
  if (param.options && param.options.length > 0) {
    if (
      hasAllSvgOptions(param.options) &&
      param.options.length <= MAX_TOGGLE_GROUP_OPTIONS
    ) {
      return 'SVG Toggle Group';
    }
    if (shouldUseToggleGroup(param.options)) return 'Toggle Group';
    return 'Select Dropdown';
  }
  if (param.type === 'file') return 'File Upload';
  if (param.type === 'color') return 'Color Picker';
  if (param.type === 'material') return 'Material Editor';
  if (param.type === 'range') return 'Range Input';
  if (param.type === 'font') return 'Font Picker';
  if (param.type === 'polygonpad') return 'Polygon Pad';
  if (param.type === 'xypad') return 'XY Pad';
  if (param.type === 'xyzpad') return 'XYZ Pad';
  if (param.type === 'angle') return 'Angle Dial';
  if (!param.type || param.type === 'number') {
    if (param.range?.min !== undefined || param.range?.max !== undefined) {
      return 'Number Slider (with range)';
    }
    return 'Number Slider';
  }
  if (param.type === 'boolean') return 'Boolean Toggle';
  if (param.type === 'string') return 'Text Input';
  if (param.type === 'string[]') return 'String Array';
  if (param.type === 'number[]') return 'Vector Sliders';
  if (param.type === 'boolean[]') return 'Boolean Array';
  return 'Unknown';
}
